import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { WalletNavigation } from 'src/app/models/WalletReports';
import { AuthService } from './auth.service';
import { WalletReportsService } from './wallet-reports.service';

/** Até a 1ª resposta chegar, o menu mostra tudo — nunca esconde por falta de dado. */
const NAVIGATION_LOADING: WalletNavigation = {
  reports_enabled: false,
  wallet_migrated: { sms: false, call: false },
  hide: { messages_report: false, lead_responses: false, recharges: false },
};

/**
 * `GET /api/wallet/navigation` como stream — a FONTE ÚNICA de "o que esconder do
 * menu" (M7, task 17tm466nz8b). Não é gated (responde mesmo com a carteira
 * desligada) e o front não recalcula a partir de flags do `user`, que o
 * `~/trabalhos/m7-coord.md` avisa ficarem desatualizadas depois que o admin migra
 * o cliente — mesmo motivo do `AccountVersionService` para o realm.
 *
 * Refaz em `userChanged`/`sessionChanged`: login, logout e entrar/sair de
 * simulação trocam de conta e a navegação é por conta, não por sessão de
 * browser. Uma falha na requisição mantém o estado anterior (ou o "mostra tudo"
 * inicial) — nunca esconde item por causa de um erro de rede.
 */
@Injectable({ providedIn: 'root' })
export class WalletNavigationService {

  private navigationSubject = new BehaviorSubject<WalletNavigation>(NAVIGATION_LOADING);

  constructor(private reportsService: WalletReportsService, private authService: AuthService) {
    this.refresh();
    this.authService.userChanged.subscribe(() => this.refresh());
    this.authService.sessionChanged.subscribe(() => this.refresh());
  }

  /** Snapshot síncrono — é o que `MenuService.getMenuItems()` lê, sem assinar nada. */
  get current(): WalletNavigation {
    return this.navigationSubject.value;
  }

  get changes(): Observable<WalletNavigation> {
    return this.navigationSubject.asObservable();
  }

  refresh(): void {
    if (!this.authService.isLogged()) {
      // Logout: "mostra tudo" até a PRÓXIMA conta logar e responder. Sem isto, o menu de
      // quem loga depois podia nascer com a regra de quem saiu (achado de QA, 14/10).
      if (this.navigationSubject.value !== NAVIGATION_LOADING) {
        this.navigationSubject.next(NAVIGATION_LOADING);
      }
      return;
    }

    this.reportsService
      .navigation()
      .pipe(catchError(() => of(null)))
      .subscribe((navigation) => {
        if (navigation) {
          this.navigationSubject.next(navigation);
        }
      });
  }
}
